import { ReactNode, useContext, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { XCircleIcon } from "@heroicons/react/24/outline";
import { AppMainStateContext } from "../../contexts/AppMainStateContext.tsx";
import { IModalProps } from "./IModalProps.ts";
import { useAppDispatch, useAppSelector } from "../../stores/store.ts";
import { modalsStateActions } from "../popups/modalsStateSlice.ts";

interface IProps extends IModalProps {
  children: ReactNode,
}

const modalContainer = document.getElementById("modal-root") as HTMLElement;

const Portal = ({ children, isOpen, onClose }: IProps) => {
  const isRequestToCloseModalSent = useAppSelector(state => state.modalsState.isRequestToCloseModalSent);
  const dispatch = useAppDispatch();
  const modalRef = useRef<HTMLDivElement>(null);
  const { isMobile, isDisabledCloseModal } = useContext(AppMainStateContext);
  const modalNode = document.createElement("div");
  useEffect(() => {
    modalContainer.appendChild(modalNode);
    return () => {
      modalContainer.removeChild(modalNode);
    };
  }, [modalNode]);

  const handleCloseClick = () => {
    if (isOpen && modalRef.current && !isDisabledCloseModal) {
      modalRef.current.setAttribute("close", "");
      setTimeout(() => onClose(), 150);
    }
  };

  useEffect(() => {
    if (isRequestToCloseModalSent) {
      dispatch(modalsStateActions.setIsRequestToCloseModalSent(false));
      onClose();
    }
  }, [isRequestToCloseModalSent]);

  const modalWrapper = (
    <div
      ref={modalRef}
      className={`popup fixed w-screen h-screen inset-0 z-50 overflow-hidden`}
      data-open={isOpen ? isOpen : undefined}
    >
      <div
        className={"relative h-full bg-neutral-900/60"}
        onClick={handleCloseClick}
      ></div>
      <div className={`absolute top-1/2 -translate-y-1/2 right-0 bg-neutral-700 left-1/2 -translate-x-1/2 rounded-3xl ${isMobile ? "w-[90%]" : "max-w-120"}`}>
        <div className={"relative"}>
          {!isDisabledCloseModal &&
            <button
              className={"absolute right-1 -top-11"}
              onClick={handleCloseClick}
            >
              <XCircleIcon className={"w-9 h-9 text-neutral-200 hover:text-white transition-colors stroke-[1.5]"} />
            </button>}
          {children}
        </div>
      </div>
    </div>
  );

  return createPortal(modalWrapper, modalNode);
};

export default Portal;